import { themeColors } from "@/utils/themeColors";
import { isDarkColor } from "@knowt/syncing/utils/genericUtils";
import noop from "@knowt/syncing/utils/noop";
import Box from "@mui/material/Box";
import { SxProps } from "@mui/material/styles";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { ConditionalLink } from "./ConditionalLink";
import ConditionalTooltip from "./ConditionalTooltip";

export type TabProps = {
    label: string;
    value?: number | string;
    node?: ReactNode;
    tooltip?: string;
    disabled?: boolean;
    disabledToast?: string;
    onClick?: () => void;
    href?: string;
    className?: string;
    node?: ReactNode;
};

export type TabSwitcherProps = {
    tabs: TabProps[];
    selectedTab?: number;
    setSelectedTab?: (index: number) => void;
    tabColor?: string | null | undefined;
    width?: string;
    height?: string;
    padding?: string;
    margin?: string;
    fontSize?: string;
    fontWeight?: string;
    color?: string;
    containerStyle?: React.CSSProperties;
    className?: string;
    alternate?: boolean;
    tabSx?: SxProps;
    textStyle?: React.CSSProperties;
};

/**
 * @deprecated use "./Tabs/Tabs.tsx" instead
 */
const TabSwitcher = ({
    tabs,
    selectedTab = 0,
    setSelectedTab = noop,
    tabColor,
    width = "13.2rem",
    height = "0",
    padding = "0",
    margin = "0",
    fontSize = "1.7rem",
    fontWeight = "600",
    color = "",
    containerStyle = {},
    className,
    alternate = false,
    tabSx,
    textStyle,
}: TabSwitcherProps) => {
    const tabRef = useRef({});
    const tabContainerRef = useRef<HTMLDivElement>(null);

    //biome-ignore lint:
    const widthValue = eval(width.split("rem")[0]);

    const horizontalPaddingAndMargin =
        //biome-ignore lint:
        eval(padding.split(" ").slice(padding.split(" ").length - 1)[0]) +
        (widthValue > 11.5 ? 0.7 : widthValue > 10 ? 0.6 : 0.5); // arbitrary values from trial-and-error

    const [hoveredTab, setHoveredTab] = useState<number | null>(null);

    const [currentTabLeft, setCurrentTabLeft] = useState(
        `${selectedTab * (widthValue + horizontalPaddingAndMargin * 2)}rem`
    );

    const handleTabChange = index => {
        if (currentTabLeft === `${index * (widthValue + horizontalPaddingAndMargin * 2)}rem`) return;

        setSelectedTab(index);
        setCurrentTabLeft(`${tabRef.current[index]?.offsetLeft - 5}px`); // 5 is from trail-and-error :)
    };

    // biome-ignore lint: useExhaustiveDependencies
    useEffect(() => {
        handleTabChange(selectedTab);
    }, [selectedTab]);

    // biome-ignore lint: useExhaustiveDependencies
    useEffect(() => {
        const resizeHandler = () => setCurrentTabLeft(`${tabRef.current[selectedTab]?.offsetLeft - 5}px`);

        resizeHandler();

        const container = tabContainerRef.current;

        // we need this listener to update the position of the current tab indicator when the window is resized as the width is changed by parent per smDown classes (example in search page)
        container?.addEventListener("resize", resizeHandler);
        window.addEventListener("resize", resizeHandler);
        return () => {
            window.removeEventListener("resize", resizeHandler);
            container?.removeEventListener("resize", resizeHandler);
        };
    }, [tabContainerRef.current, tabRef.current, selectedTab]);

    return (
        <div
            ref={tabContainerRef}
            className={className}
            data-testid="tab-switcher"
            style={{
                position: "relative",
                display: "inline-flex",
                borderRadius: 50,
                backgroundColor: alternate ? themeColors.neutral1 : themeColors.neutralWhite,
                margin: margin,
                height: height || undefined,
                padding: "0 0.6rem",
                flexShrink: 0,
                ...containerStyle,
            }}>
            {tabs.map(({ label, node, tooltip, disabled = false, onClick, href, className, disabledToast }, index) => (
                <ConditionalTooltip key={index + "-" + selectedTab} tooltip={tooltip}>
                    <Box
                        component={href ? "span" : "div"}
                        // biome-ignore lint/suspicious/noAssignInExpressions:
                        ref={ref => {
                            tabRef.current = { ...tabRef.current, [index]: ref };
                        }}
                        onMouseEnter={() => setHoveredTab(index)}
                        onMouseLeave={() => setHoveredTab(null)}
                        sx={{
                            minWidth: width,
                            maxWidth: width,
                            padding,
                            borderRadius: 50,
                            margin: `0.5rem`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: alternate ? themeColors.neutral1 : themeColors.neutralWhite,
                            cursor: disabled ? "default" : "pointer",
                            transition: "background-color 0.2s",
                            ...(selectedTab !== index && {
                                "&:hover": {
                                    backgroundColor: alternate ? themeColors.neutral2 : themeColors.neutral1,
                                },
                            }),
                            ...tabSx,
                        }}
                        onClick={() => {
                            if (href) return;
                            if (disabled) return disabledToast ? toast.error(disabledToast) : null;

                            handleTabChange(index);
                            if (onClick) onClick();
                        }}>
                        <span
                            className={className}
                            style={{
                                fontFamily: "var(--knowt-font-name)",
                                fontSize: fontSize || "1.8rem",
                                fontWeight: fontWeight,
                                color:
                                    color || selectedTab === index
                                        ? tabColor
                                            ? isDarkColor(tabColor)
                                                ? themeColors.pureWhite
                                                : themeColors.pureBlack
                                            : alternate
                                              ? themeColors.neutralBlack
                                              : themeColors.neutralWhite
                                        : hoveredTab === index
                                          ? themeColors.neutralBlack
                                          : themeColors.neutral3,
                                zIndex: 1,
                                opacity: disabled ? 0.5 : 1,
                                minWidth: width,
                                maxWidth: width,
                                textAlign: "center",
                                ...textStyle,
                            }}
                            data-testid="auto-id-b0f2e0b0-6d11-4e80-b650-23ff96a6b0d4">
                            <ConditionalLink
                                key={label}
                                condition={!!href}
                                href={href}
                                style={{
                                    color:
                                        color || selectedTab === index
                                            ? tabColor
                                                ? isDarkColor(tabColor)
                                                    ? themeColors.pureWhite
                                                    : themeColors.pureBlack
                                                : alternate
                                                  ? themeColors.neutralBlack
                                                  : themeColors.neutralWhite
                                            : hoveredTab === index
                                              ? themeColors.neutralBlack
                                              : themeColors.neutral3,
                                    ...textStyle,
                                }}>
                                {node ?? label}
                            </ConditionalLink>
                        </span>
                    </Box>
                </ConditionalTooltip>
            ))}
            <div
                style={{
                    position: "absolute",
                    top: "4px",
                    bottom: "4px",
                    backgroundColor: tabColor || (alternate ? themeColors.neutralWhite : themeColors.neutralBlack),
                    borderRadius: "4.5rem",
                    willChange: "transform",
                    pointerEvents: "none",
                    width: width,
                    transform: `translateX(${currentTabLeft})`,
                    transition: "transform ease 0.4s",
                }}
                data-testid="auto-id-3de1c556-3655-40dd-b2d1-a7a1108945f0"
            />
        </div>
    );
};

export default TabSwitcher;
